import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardHeader, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { paymentAPI, PaymentMethod } from '../../services/subscriptionApi';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PaymentFormProps {
  orderId: string;
  amount: number;
  onSubmit?: (data: PaymentFormValues) => Promise<void>;
  onSuccess: () => void;
}

export interface PaymentFormValues {
  transactionId: string;
  paymentMethod: PaymentMethod;
  paymentProof?: File;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ALLOWED_FILE_TYPES = ['image/png', 'image/jpg', 'image/jpeg'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const TRANSACTION_ID_PATTERN = /^[A-Za-z0-9\-_]{5,100}$/;

// ─── Component ────────────────────────────────────────────────────────────────

const PaymentForm = ({ orderId, amount, onSubmit, onSuccess }: PaymentFormProps) => {
  // ── Form state ─────────────────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PaymentFormValues>({
    defaultValues: {
      transactionId: '',
      paymentMethod: 'upi',
    },
  });

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // File state (managed outside RHF because Input component doesn't support file)
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofPreview, setProofPreview] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const transactionId = watch('transactionId');

  // ── File handling ──────────────────────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setFileError(null);
    setProofFile(null);
    setProofPreview(null);

    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setFileError('Only PNG, JPG, and JPEG files are allowed.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError('File size must not exceed 5 MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setProofFile(file);
    setProofPreview(URL.createObjectURL(file));
  };

  const handleRemoveFile = () => {
    setProofFile(null);
    setProofPreview(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Submission ─────────────────────────────────────────────────────────────

  const handleFormSubmit = async (values: PaymentFormValues) => {
    setSubmitError(null);
    setLoading(true);

    try {
      const payload = {
        orderId,
        transactionId: values.transactionId.trim(),
        paymentMethod: values.paymentMethod,
        ...(proofFile ? { paymentProof: proofFile } : {}),
      };

      if (onSubmit) {
        // Custom submit handler (e.g. for testing)
        await onSubmit({ ...values, paymentProof: proofFile ?? undefined });
      } else {
        await paymentAPI.submitPayment(payload);
      }

      setSubmitted(true);
      onSuccess();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to submit payment.';

      // Map known API error messages to user-friendly text
      if (message.toLowerCase().includes('transaction id has already been used')) {
        setSubmitError('This transaction ID has already been used. Please check and try again.');
      } else if (
        message.toLowerCase().includes('already submitted') ||
        message.toLowerCase().includes('duplicate order')
      ) {
        setSubmitError('Payment details have already been submitted for this order.');
      } else {
        setSubmitError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Success state ──────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center py-6 text-center gap-3">
            <div className="text-4xl">✅</div>
            <h3 className="text-lg font-semibold text-gray-900">Payment under review</h3>
            <p className="text-sm text-gray-600 max-w-sm">
              Your payment details have been submitted. Our team will verify and activate your
              subscription shortly.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── Form render ────────────────────────────────────────────────────────────

  return (
    <Card>
      <CardHeader
        title="Submit Payment Details"
        subtitle="Enter your transaction ID after completing the payment. Uploading a screenshot is recommended for faster verification."
      />
      <CardContent>
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate className="space-y-6">

          {/* ── Payment Method ─────────────────────────────────────────── */}
          <fieldset>
            <legend className="form-label mb-2">
              Payment Method <span className="text-danger-600 ml-1">*</span>
            </legend>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="upi"
                  {...register('paymentMethod', { required: true })}
                  className="accent-primary-600"
                />
                <span className="text-sm font-medium text-gray-700">UPI</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="bank_transfer"
                  {...register('paymentMethod', { required: true })}
                  className="accent-primary-600"
                />
                <span className="text-sm font-medium text-gray-700">Bank Transfer</span>
              </label>
            </div>
          </fieldset>

          {/* ── Transaction ID ─────────────────────────────────────────── */}
          <div>
            <Input
              label="Transaction ID / UTR Number"
              placeholder="e.g. TXN123456789 or UTR123456789012"
              value={transactionId}
              onChange={(val) => setValue('transactionId', val, { shouldValidate: true })}
              required
              error={errors.transactionId?.message}
              helpText="Enter the UPI transaction ID or bank UTR number from your payment confirmation."
              disabled={loading}
            />
            {/* Hidden input for RHF registration with validation */}
            <input
              type="hidden"
              {...register('transactionId', {
                required: 'Transaction ID is required.',
                minLength: { value: 5, message: 'Transaction ID must be at least 5 characters.' },
                maxLength: { value: 100, message: 'Transaction ID must not exceed 100 characters.' },
                pattern: {
                  value: TRANSACTION_ID_PATTERN,
                  message: 'Transaction ID may only contain letters, numbers, hyphens, and underscores.',
                },
                validate: (v) => v.trim().length > 0 || 'Transaction ID cannot be blank.',
              })}
            />
          </div>

          {/* ── Payment Proof Upload ───────────────────────────────────── */}
          <div>
            <p className="form-label mb-2">
              Payment Screenshot{' '}
              <span className="text-xs font-normal text-gray-500">(optional, recommended)</span>
            </p>

            {!proofFile ? (
              <label
                htmlFor="payment-proof-input"
                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  fileError
                    ? 'border-red-400 bg-red-50 hover:bg-red-100'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className="flex flex-col items-center gap-1 text-center px-4">
                  <span className="text-2xl">📎</span>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium text-primary-600">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-400">PNG, JPG, JPEG — max 5 MB</p>
                </div>
                <input
                  id="payment-proof-input"
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleFileChange}
                  disabled={loading}
                  className="hidden"
                  aria-label="Upload payment screenshot"
                />
              </label>
            ) : (
              <div className="relative inline-block">
                <img
                  src={proofPreview!}
                  alt="Payment proof preview"
                  className="h-32 w-auto rounded-lg border border-gray-200 object-cover"
                />
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  disabled={loading}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                  aria-label="Remove uploaded file"
                >
                  ✕
                </button>
                <p className="mt-1 text-xs text-gray-500 truncate max-w-[200px]">{proofFile.name}</p>
              </div>
            )}

            {fileError && (
              <p className="form-error mt-1" role="alert">
                {fileError}
              </p>
            )}
          </div>

          {/* ── Amount reminder ────────────────────────────────────────── */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            Submitting payment of{' '}
            <span className="font-bold">₹{amount.toLocaleString('en-IN')}</span> for order{' '}
            <span className="font-mono font-semibold">{orderId}</span>.
          </div>

          {/* ── API error ──────────────────────────────────────────────── */}
          {submitError && (
            <div
              className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700"
              role="alert"
            >
              {submitError}
            </div>
          )}

          {/* ── Submit button ──────────────────────────────────────────── */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={loading}
            disabled={loading || submitted}
          >
            {loading ? 'Submitting...' : 'Submit Payment Details'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default PaymentForm;
